import { InternalId } from '@edgebox/data-definition-kit';
import { TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { sendToParent } from '../../../frame-messages';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { BRAND_PRESENCE_ONBOARDING_TOTAL_STEPS } from './brandPresencePaths';
import { GovernanceTaxonomySelection } from './Components/GovernanceTaxonomySelection';
import {
  ITaxonomyFacetSuggestion,
  buildTaxonomySelectionGroups,
  createTaxonomiesForFacets,
  extractTaxonomyRecommendations,
  recommendedFacetsForOrganizationType,
} from './taxonomySelection.utils';
import { GovernanceOnboardingContainer, GovernanceOnboardingStepContainer } from './Onboarding/components/GovernanceOnboardingPrimitives';
import { GovernanceOnboardingListSkeleton } from './Onboarding/components/GovernanceOnboardingSkeleton';

export function GovernanceOnboardingStepTaxonomies() {
  const navigate = useNavigate();
  const apiContext = React.useContext(SyncCoreApiContext);
  const params = useParams<{ onboardingId?: string }>();
  const [searchParams] = useSearchParams();
  const taskGroupId = params.onboardingId || searchParams.get('taskGroupId') || undefined;

  const [existingFacets, setExistingFacets] = React.useState<TaxonomyFacet[]>([]);
  const [aiSuggestions, setAiSuggestions] = React.useState<ITaxonomyFacetSuggestion[]>([]);
  const [organizationTypeFacets, setOrganizationTypeFacets] = React.useState<readonly TaxonomyFacet[]>(
    recommendedFacetsForOrganizationType(undefined)
  );
  const [selectedFacets, setSelectedFacets] = React.useState<TaxonomyFacet[]>([]);
  const [ignoredFacets, setIgnoredFacets] = React.useState<TaxonomyFacet[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!apiContext?.api || !taskGroupId) {
      setLoading(false);
      setError('Missing onboarding task group. Please start from Step 1.');
      return;
    }

    let active = true;
    void (async () => {
      setLoading(true);
      setError(undefined);

      try {
        const onboardingTaskGroup = await apiContext.api.service.taskGroups.item(taskGroupId as InternalId, true);
        const project = await onboardingTaskGroup.project?.get();
        const projectId = project?.id as InternalId | undefined;

        const existingProjectTaxonomies = projectId
          ? await apiContext.api.service.taxonomies.list({ page: 0, itemsPerPage: 500 }, { projectId })
          : { items: [] };

        const nextExistingFacets = (existingProjectTaxonomies.items || [])
          .map((taxonomy) => taxonomy.facet as TaxonomyFacet | undefined)
          .filter((facet): facet is TaxonomyFacet => Boolean(facet));

        const extraction = await apiContext.api.governance.onboarding.mainPageDataExtraction({
          taskGroupId: taskGroupId as InternalId,
        });

        if (!active) {
          return;
        }

        const recommendations = extractTaxonomyRecommendations(extraction);

        setExistingFacets(nextExistingFacets);
        setAiSuggestions(recommendations.aiSuggestions);
        setOrganizationTypeFacets(recommendations.organizationTypeFacets);
      } catch (loadError) {
        if (!active) {
          return;
        }

        setError((loadError as Error)?.message || 'Unable to load taxonomy recommendations.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [apiContext?.api, taskGroupId]);

  const groups = React.useMemo(
    () =>
      buildTaxonomySelectionGroups({
        existingFacets,
        aiSuggestions,
        organizationTypeFacets,
        selectedFacets,
        ignoredFacets,
        showRecommendationInfo: true,
      }),
    [aiSuggestions, existingFacets, ignoredFacets, organizationTypeFacets, selectedFacets]
  );

  const acceptFacet = (facet: TaxonomyFacet) => {
    setIgnoredFacets((prev) => prev.filter((candidate) => candidate !== facet));
    setSelectedFacets((prev) => (prev.includes(facet) ? prev : [...prev, facet]));
  };

  const ignoreFacet = (facet: TaxonomyFacet) => {
    setIgnoredFacets((prev) => (prev.includes(facet) ? prev : [...prev, facet]));
  };

  const removeFacet = (facet: TaxonomyFacet) => {
    setSelectedFacets((prev) => prev.filter((candidate) => candidate !== facet));
  };

  const onContinue = async () => {
    if (!apiContext?.api || !taskGroupId) {
      return;
    }

    setSubmitting(true);
    setError(undefined);
    try {
      const taskGroup = await apiContext.api.service.taskGroups.item(taskGroupId as InternalId, true);
      const project = await taskGroup.project?.get();
      const projectId = project?.id as InternalId | undefined;
      if (!projectId) {
        throw new Error('Could not resolve project for onboarding.');
      }

      await createTaxonomiesForFacets({
        api: apiContext.api,
        facets: selectedFacets,
        projectId,
        customer: taskGroup.customer,
        project: taskGroup.project,
      });

      sendToParent({
        type: 'update-path',
        path: `/brand-presence/onboarding/${encodeURIComponent(taskGroupId)}/taxonomy-terms-primary`,
      });
      navigate(`/brand-presence/onboarding/${encodeURIComponent(taskGroupId)}/taxonomy-terms-primary`);
    } catch (continueError) {
      setError((continueError as Error)?.message || 'Unable to save selected taxonomies.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <GovernanceOnboardingContainer>
      <GovernanceOnboardingStepContainer
        step={3}
        totalSteps={BRAND_PRESENCE_ONBOARDING_TOTAL_STEPS}
        title={'Select your taxonomies'}
        description={
          'Choose the dimensions you need for analytics. Content will be automatically tagged for you. You can select the terms in the next step.'
        }
        onBack={() => {
          if (taskGroupId) {
            sendToParent({
              type: 'update-path',
              path: `/brand-presence/onboarding/${encodeURIComponent(taskGroupId)}/key-content`,
            });
            navigate(`/brand-presence/onboarding/${encodeURIComponent(taskGroupId)}/key-content`, { replace: true });
            return;
          }

          navigate('/brand-presence/onboarding/key-content', { replace: true });
        }}
        onContinue={() => {
          void onContinue();
        }}
        continueDisabled={loading || submitting}
        continueLabel={submitting ? 'Saving…' : 'Continue'}
      >
        <div className="w-full">
          {loading ? <GovernanceOnboardingListSkeleton label="Loading recommended taxonomies…" /> : null}

          {error ? <div className="mb-6 text-sm text-danger">{error}</div> : null}

          {!loading ? (
            <div className="mb-8">
              <GovernanceTaxonomySelection
                groups={groups}
                onAccept={acceptFacet}
                onIgnore={ignoreFacet}
                onRemove={removeFacet}
                showRecommendationInfo
                expandAllCategories={false}
              />
            </div>
          ) : null}
        </div>
      </GovernanceOnboardingStepContainer>
    </GovernanceOnboardingContainer>
  );
}
